/**
 * Drop status utilities
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ReactElement } from 'react';
import { CheckCircle2, Clock, FileText, PauseCircle, Play, XCircle } from 'lucide-react';

import type { DropStatusLabel, SmswiserDrop } from '@/types/smswiser-drops';

export function deriveDropStatusLabel(drop: SmswiserDrop): DropStatusLabel {
  if (drop.status?.isDraft) return 'draft';
  if (drop.status?.Paused) return 'paused';
  if (drop.status?.Stopped) return 'stopped';

  if (drop.finishedAt) return 'completed';

  if (drop.executeAfter) {
    const startedAt = (drop.status?.startedAt || '').trim?.() ?? '';
    if (!startedAt) return 'scheduled';
  }

  const startedAt = (drop.status?.startedAt || '').trim?.() ?? '';
  if (!startedAt) return 'pending';

  return 'started';
}

export function getStatusBadgeClasses(status: DropStatusLabel): string {
  const classes: Record<DropStatusLabel, string> = {
    started: 'bg-info/10 text-info',
    pending: 'bg-warning/10 text-warning',
    scheduled: 'bg-warning/10 text-warning',
    completed: 'bg-success/10 text-success',
    paused: 'bg-warning/10 text-warning',
    stopped: 'bg-destructive/10 text-destructive',
    draft: 'bg-muted text-muted-foreground',
  };
  return classes[status] ?? 'bg-muted text-muted-foreground';
}

export function getStatusIcon(status: DropStatusLabel): ReactElement {
  const base = 'h-4 w-4 shrink-0';

  switch (status) {
    case 'completed':
      return <CheckCircle2 className={`${base} text-success`} aria-hidden="true" />;
    case 'started':
      return <Play className={`${base} text-info`} aria-hidden="true" />;
    case 'pending':
      return <Clock className={`${base} text-warning`} aria-hidden="true" />;
    case 'scheduled':
      return <Clock className={`${base} text-warning`} aria-hidden="true" />;
    case 'paused':
      return <PauseCircle className={`${base} text-warning`} aria-hidden="true" />;
    case 'stopped':
      return <XCircle className={`${base} text-destructive`} aria-hidden="true" />;
    case 'draft':
      return <FileText className={`${base} text-muted-foreground`} aria-hidden="true" />;
    default:
      return <Clock className={`${base} text-muted-foreground`} aria-hidden="true" />;
  }
}

